/**
 * Smart address parser — like SF Express app.
 * Paste a blob of text and auto-extract name, phone, province, city, district, detail.
 *
 * Handles formats like:
 *   "张三 13800138000 广东省深圳市南山区科技南路18号xx花园3栋1201"
 *   "张三,138-0013-8000,广东省深圳市南山区 科技南路18号"
 *   "收件人：张三 手机：13800138000 地址：广东省深圳市南山区科技南路18号"
 */

// Common Chinese province names (with and without 省/市 suffix)
const PROVINCES = [
  '北京','天津','上海','重庆',
  '河北','山西','辽宁','吉林','黑龙江',
  '江苏','浙江','安徽','福建','江西','山东',
  '河南','湖北','湖南','广东','海南',
  '四川','贵州','云南','陕西','甘肃','青海',
  '台湾','内蒙古','广西','西藏','宁夏','新疆','香港','澳门',
];

const PROVINCE_SUFFIXES = ['省', '市', '自治区', '特别行政区', '壮族自治区', '回族自治区', '维吾尔自治区'];

// Direct-administered municipalities
const MUNICIPALITIES = ['北京', '天津', '上海', '重庆'];

export default function parseAddress(raw) {
  if (!raw || typeof raw !== 'string') return null;

  // Normalize: remove common labels, replace separators
  let text = raw
    .replace(/收件人[：:]\s*/g, '')
    .replace(/姓名[：:]\s*/g, '')
    .replace(/手机[号]?[：:]\s*/g, '')
    .replace(/电话[：:]\s*/g, '')
    .replace(/地址[：:]\s*/g, '')
    .replace(/邮编[：:]\s*/g, '')
    .replace(/[,，;；\t\n\r]+/g, ' ')
    .trim();

  const result = { name: '', phone: '', province: '', city: '', district: '', detail: '' };

  // 1. Extract phone number (11-digit mobile or with dashes/spaces)
  const phoneRe = /1[3-9]\d[\s-]?\d{4}[\s-]?\d{4}/;
  const phoneMatch = text.match(phoneRe);
  if (phoneMatch) {
    result.phone = phoneMatch[0].replace(/[\s-]/g, '');
    text = text.replace(phoneMatch[0], ' ').trim();
  }

  // 2. Extract postal code (6 digits)
  const postalRe = /\b(\d{6})\b/;
  const postalMatch = text.match(postalRe);
  if (postalMatch) {
    result.postal = postalMatch[1];
    text = text.replace(postalMatch[0], ' ').trim();
  }

  // 3. Try to find province in remaining text
  let addrStart = -1;
  for (const prov of PROVINCES) {
    // Match province with or without suffix
    const provRe = new RegExp(prov + '(?:' + PROVINCE_SUFFIXES.join('|') + ')?');
    const m = text.match(provRe);
    if (m) {
      result.province = m[0];
      addrStart = text.indexOf(m[0]);
      break;
    }
  }

  // If we found province, everything before it (excluding phone) is the name
  if (addrStart > 0) {
    const beforeAddr = text.substring(0, addrStart).trim();
    if (beforeAddr && beforeAddr.length <= 10) {
      result.name = beforeAddr;
    }
    text = text.substring(addrStart);
  } else if (addrStart === 0) {
    // Province at start, name might be absent — try to extract from original
  }

  // 4. Parse province/city/district from the address portion
  let addrText = text.trim();

  if (result.province) {
    addrText = addrText.substring(result.province.length).trim();
  }

  // Try to extract city
  const cityRe = /^(.{2,6}?(?:市|自治州|州|地区|盟))/;
  const cityMatch = addrText.match(cityRe);
  if (cityMatch) {
    result.city = cityMatch[1];
    addrText = addrText.substring(cityMatch[0].length).trim();
  } else if (MUNICIPALITIES.some(m => result.province?.startsWith(m))) {
    // For municipalities, city = province
    result.city = result.province.replace(/[市省]$/, '') + '市';
  }

  // Try to extract district
  const distRe = /^(.{2,6}?(?:区|县|市|旗))/;
  const distMatch = addrText.match(distRe);
  if (distMatch) {
    result.district = distMatch[1];
    addrText = addrText.substring(distMatch[0].length).trim();
  }

  // 5. Remaining = detail address
  result.detail = addrText.trim();

  // 6. If name still empty, try to extract from what's left
  if (!result.name) {
    // Split by spaces, first token that looks like a name (2-4 Chinese chars)
    const tokens = raw.replace(phoneRe, '').replace(/[,，;；\t\n\r]+/g, ' ').trim().split(/\s+/);
    for (const tok of tokens) {
      const cleaned = tok.replace(/收件人|姓名|[：:]/g, '');
      if (/^[\u4e00-\u9fa5]{2,4}$/.test(cleaned)) {
        // Make sure it's not a province/city name
        if (!PROVINCES.some(p => cleaned.startsWith(p)) && !cleaned.match(/省|市|区|县/)) {
          result.name = cleaned;
          break;
        }
      }
    }
  }

  // Only return if we extracted at least phone or address
  if (!result.phone && !result.detail && !result.province) return null;

  return result;
}
